/*
 * IPPO Pro product page components.
 * Works with Dawn's <product-form>: before adding to cart, the form dispatches
 * "lx:collect-items" so these components can add extra lines or replace the main line.
 */
(() => {
  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  const qbState = (window.lxQbState = window.lxQbState || {});

  /* Re-render blocks marked data-lx-refresh when the variant changes. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data || !data.html) return;
      document.querySelectorAll(`[data-lx-refresh][data-section="${data.sectionId}"]`).forEach((el) => {
        const source = data.html.getElementById(el.id);
        if (source) el.innerHTML = source.innerHTML;
      });
      document.querySelectorAll(`[data-lx-btn-price="${data.sectionId}"]`).forEach((el) => {
        if (data.variant) el.textContent = window.lxFormatMoney(data.variant.price);
      });
      document.dispatchEvent(new CustomEvent('lx:variant-change', { detail: data }));
    });
  }

  const formIdOf = (form) => (form && form.getAttribute ? form.getAttribute('id') : null);
  const forForm = (event, formId) => formIdOf(event.target) === formId;

  /* ------------------------------------------------------ Quantity breaks */
  class LxQtyBreaks extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form;
      this.key = `${this.formId}:${this.dataset.block}`;
      const ownInput = this.querySelector('[data-lx-qb-qty]');
      const external = document.querySelector(`input.quantity__input[form="${this.formId}"]`);
      if (external && ownInput) {
        ownInput.remove();
        this.qtyInput = external;
      } else {
        this.qtyInput = ownInput;
      }
      const remembered = qbState[this.key];
      const options = Array.from(this.querySelectorAll('.lx-qb__option'));
      const target = options.find((o) => o.dataset.index === String(remembered)) || options.find((o) => o.querySelector('input:checked')) || options[0];
      if (target) target.querySelector('.lx-qb__radio').checked = true;
      this.addEventListener('change', (e) => {
        if (e.target.matches('.lx-qb__radio')) this.apply();
      });
      this.onCollect = (e) => {
        if (!forForm(e, this.formId)) return;
        const selected = this.selectedOption();
        const selects = selected ? Array.from(selected.querySelectorAll('[data-lx-qb-select]')) : [];
        if (selects.length > 1) {
          const counts = {};
          selects.forEach((s) => (counts[s.value] = (counts[s.value] || 0) + 1));
          e.detail.replaceMain = Object.entries(counts).map(([id, quantity]) => ({ id: parseInt(id, 10), quantity }));
        }
      };
      document.addEventListener('lx:collect-items', this.onCollect);
      this.apply();
    }
    disconnectedCallback() {
      document.removeEventListener('lx:collect-items', this.onCollect);
    }
    selectedOption() {
      return this.querySelector('.lx-qb__radio:checked')?.closest('.lx-qb__option');
    }
    apply() {
      const selected = this.selectedOption();
      if (!selected) return;
      qbState[this.key] = selected.dataset.index;
      this.querySelectorAll('.lx-qb__option').forEach((o) => {
        const on = o === selected;
        o.classList.toggle('is-selected', on);
        const variants = o.querySelector('[data-lx-qb-variants]');
        if (variants) variants.hidden = !on;
      });
      const qty = parseInt(selected.dataset.qty, 10) || 1;
      if (this.qtyInput) {
        this.qtyInput.value = qty;
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      document.dispatchEvent(new CustomEvent('lx:qty-change', { detail: { form: this.formId, quantity: qty } }));
    }
  }
  define('lx-qty-breaks', LxQtyBreaks);

  /* Quantity typed in Dawn's selector also drives gifts. */
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input.matches || !input.matches('input.quantity__input[form]')) return;
    document.dispatchEvent(new CustomEvent('lx:qty-change', { detail: { form: input.getAttribute('form'), quantity: parseInt(input.value, 10) || 1 } }));
  });

  /* ------------------------------------------------------ Gifts on quantity */
  class LxQtyGifts extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form;
      this.quantity = 1;
      const qtyInput = document.querySelector(`[form="${this.formId}"][name="quantity"]`);
      if (qtyInput) this.quantity = parseInt(qtyInput.value, 10) || 1;
      this.onQty = (e) => {
        if (e.detail.form !== this.formId) return;
        this.quantity = e.detail.quantity;
        this.update();
      };
      this.onCollect = (e) => {
        if (!forForm(e, this.formId)) return;
        this.unlocked().forEach((g) => {
          if (g.dataset.variantId) e.detail.items.push({ id: parseInt(g.dataset.variantId, 10), quantity: 1, properties: { _gift: 'true' } });
        });
      };
      document.addEventListener('lx:qty-change', this.onQty);
      document.addEventListener('lx:collect-items', this.onCollect);
      this.update();
    }
    disconnectedCallback() {
      document.removeEventListener('lx:qty-change', this.onQty);
      document.removeEventListener('lx:collect-items', this.onCollect);
    }
    unlocked() {
      return Array.from(this.querySelectorAll('[data-lx-gift]')).filter((g) => this.quantity >= parseInt(g.dataset.min || '1', 10));
    }
    update() {
      const on = this.unlocked();
      this.querySelectorAll('[data-lx-gift]').forEach((g) => g.classList.toggle('is-unlocked', on.includes(g)));
    }
  }
  define('lx-qty-gifts', LxQtyGifts);

  /* --------------------------------------------------------------- Upsells */
  class LxUpsells extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form;
      this.addEventListener('change', (e) => {
        const label = e.target.closest('.lx-upsell');
        if (!label) return;
        if (e.target.matches('[data-lx-upsell-select]')) {
          label.querySelector('[data-lx-upsell]').dataset.variantId = e.target.value;
        }
        label.classList.toggle('is-selected', label.querySelector('[data-lx-upsell]').checked);
      });
      this.addEventListener('click', (e) => {
        if (e.target.closest('select')) e.preventDefault();
      });
      this.onCollect = (e) => {
        if (!forForm(e, this.formId)) return;
        this.querySelectorAll('[data-lx-upsell]:checked').forEach((c) => {
          const id = parseInt(c.dataset.variantId, 10);
          if (id) e.detail.items.push({ id, quantity: 1 });
        });
      };
      document.addEventListener('lx:collect-items', this.onCollect);
    }
    disconnectedCallback() {
      document.removeEventListener('lx:collect-items', this.onCollect);
    }
  }
  define('lx-upsells', LxUpsells);

  /* Bundle offer in "checkbox" mode adds ticked extra products with the main add to cart. */
  document.addEventListener('lx:collect-items', (e) => {
    document.querySelectorAll('lx-bundle[data-mode="checkbox"]').forEach((bundle) => {
      if (!forForm(e, bundle.dataset.form)) return;
      bundle.querySelectorAll('[data-lx-bundle-item]').forEach((item, index) => {
        if (index === 0) return;
        const box = item.querySelector('input[type="checkbox"]');
        if (box && !box.checked) return;
        const id = parseInt(item.querySelector('select')?.value || item.dataset.variantId, 10);
        if (id) e.detail.items.push({ id, quantity: 1 });
      });
    });
  });

  /* Main product line in a bundle (button mode) follows the selected variant. */
  document.addEventListener('lx:variant-change', () => {
    document.querySelectorAll('lx-bundle').forEach((b) => b.update && b.update());
  });

  /* ------------------------------------------------- Required custom fields */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      const formId = form.getAttribute && form.getAttribute('id');
      if (!formId || !form.matches('form[data-type="add-to-cart-form"]')) return;
      let firstInvalid = null;
      document.querySelectorAll('[data-lx-field][data-lx-required]').forEach((wrap) => {
        const fields = Array.from(wrap.querySelectorAll(`[form="${formId}"]`));
        if (!fields.length) return;
        let ok;
        if (fields[0].type === 'radio' || fields[0].type === 'checkbox') ok = fields.some((f) => f.checked);
        else ok = fields.every((f) => String(f.value).trim() !== '');
        const err = wrap.querySelector('.lx-cfield__error');
        if (err) err.hidden = ok;
        wrap.classList.toggle('is-invalid', !ok);
        if (!ok && !firstInvalid) firstInvalid = wrap;
      });
      if (firstInvalid) {
        e.preventDefault();
        e.stopImmediatePropagation();
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    true
  );

  /* ---------------------------------------------------- Clickable discount */
  class LxClickDiscount extends HTMLElement {
    connectedCallback() {
      this.code = this.dataset.code;
      this.key = `lx-discount-${this.code}`;
      this.btn = this.querySelector('[data-lx-apply]');
      this.done = this.querySelector('.lx-cdisc__done');
      let applied = false;
      try {
        applied = localStorage.getItem(this.key) === '1';
      } catch (e) {}
      if (applied) this.showApplied();
      this.btn?.addEventListener('click', async () => {
        this.btn.classList.add('loading');
        try {
          await fetch(`/discount/${encodeURIComponent(this.code)}`, { credentials: 'same-origin' });
          try {
            localStorage.setItem(this.key, '1');
          } catch (e) {}
          this.showApplied();
          document.dispatchEvent(new CustomEvent('lx:discount-applied', { detail: { code: this.code } }));
        } finally {
          this.btn.classList.remove('loading');
        }
      });
    }
    showApplied() {
      if (this.btn) this.btn.hidden = true;
      if (this.done) this.done.hidden = false;
    }
  }
  define('lx-click-discount', LxClickDiscount);

  /* ------------------------------------------------------- Sticky add to cart */
  class LxStickyAtc extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.form;
      this.sectionId = this.dataset.section;
      if (this.parentElement !== document.body) {
        document.querySelectorAll(`body > lx-sticky-atc[data-section="${this.sectionId}"]`).forEach((old) => old.remove());
        document.body.appendChild(this);
        return;
      }
      this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.btn = this.querySelector('[data-lx-satc-btn]');
      this.select = this.querySelector('[data-lx-satc-select]');
      if (this.dataset.when === 'always' || this.dataset.design === 'true' || !this.mainButton) {
        this.toggle(true);
      } else {
        this.onScroll = () => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => {
            const rect = this.mainButton.getBoundingClientRect();
            this.toggle(rect.bottom < 0);
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.onScroll();
      }
      this.btn?.addEventListener('click', () => this.onClick());
      this.select?.addEventListener('change', () => this.syncVariant());
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.body.classList.remove('lx-satc-visible');
    }
    toggle(show) {
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('lx-satc-visible', show);
    }
    onClick() {
      const form = document.getElementById(this.formId);
      if (this.dataset.mode === 'scroll' || !form) {
        (this.mainButton || form)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (this.mainButton?.disabled) return;
      if (form.requestSubmit) form.requestSubmit(this.mainButton || undefined);
      else this.mainButton?.click();
    }
    syncVariant() {
      const option = this.select.selectedOptions[0];
      const values = (option?.dataset.options || '').split('|');
      const picker = document.querySelector(`#variant-selects-${this.sectionId}`) || document.querySelector('variant-selects');
      if (!picker) return;
      const groups = picker.querySelectorAll('fieldset, .product-form__input--dropdown');
      groups.forEach((group, i) => {
        const value = values[i];
        if (value === undefined) return;
        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
        const select = group.querySelector('select');
        if (select) {
          const opt = Array.from(select.options).find((o) => o.value === value || o.textContent.trim() === value);
          if (opt && select.value !== opt.value) {
            select.value = opt.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }
  }
  define('lx-sticky-atc', LxStickyAtc);

  /* ------------------------------------------------------- Delivery dates */
  const lang = () => {
    const l = document.documentElement.lang || 'en';
    return l.startsWith('ar') ? 'ar-TN' : l;
  };
  const addDays = (n, skip) => {
    const skipDays = { sun: [0], sat_sun: [0, 6], fri: [5], none: [] }[skip] || [];
    const d = new Date();
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (!skipDays.includes(d.getDay())) added++;
    }
    while (skipDays.includes(d.getDay())) d.setDate(d.getDate() + 1);
    return d;
  };
  const fmt = (d, format) => {
    const opts =
      format === 'long'
        ? { weekday: 'long', day: 'numeric', month: 'long' }
        : format === 'numeric'
          ? { day: '2-digit', month: '2-digit' }
          : { weekday: 'short', day: 'numeric', month: 'short' };
    try {
      return new Intl.DateTimeFormat(lang(), opts).format(d);
    } catch (e) {
      return d.toLocaleDateString();
    }
  };
  const renderDates = (root = document) => {
    root.querySelectorAll('[data-lx-dates]').forEach((el) => {
      const cfg = el.closest('[data-lx-date-cfg]') || el;
      const skip = cfg.dataset.skip || 'none';
      const format = cfg.dataset.format || 'short';
      const min = parseInt(el.dataset.min || '0', 10);
      const max = parseInt(el.dataset.max || String(min), 10);
      el.querySelectorAll('[data-lx-start]').forEach((s) => (s.textContent = fmt(min === 0 ? new Date() : addDays(min, skip), format)));
      el.querySelectorAll('[data-lx-end]').forEach((s) => (s.textContent = fmt(max === 0 ? new Date() : addDays(max, skip), format)));
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => renderDates());
  else renderDates();
  document.addEventListener('shopify:section:load', (e) => renderDates(e.target));
  document.addEventListener('shopify:block:select', () => renderDates());
})();
